import * as FS from '@fullstory/browser';
import { useEffect } from 'react';
import { Config } from 'src/js/config';
import systemInfo from 'src/js/data/systemInfo';
import { constant } from 'src/js/constant';
import systemStatus from '../data/systemStatus';
import { StreamState } from '../pages/streaming/stream/stream-models';
import streamModels from '../pages/streaming/stream/stream-store';
import videoEncoders from '../pages/streaming/video-encoder/video-encoder-store';
import audioEncoders from '../pages/streaming/audio-encoder/audio-encoder-store';
import { AudioEncoderState } from '../pages/streaming/audio-encoder/audio-encoder-models';
import { VideoEncoderState } from '../pages/streaming/video-encoder/video-encoder-models';
import metadataModels from '../pages/streaming/metadata/metadata-store';
import { MetadataState } from '../pages/streaming/metadata/metadata-models';
import scteModels from '../pages/streaming/scte/scte-store';
import { ScteState } from '../pages/streaming/scte/scte-models';
import { logoSettingsStore, logoStore } from '../pages/streaming/logos/logos-store';
import { licensesList } from '../pages/settings/license/licenses';
import { dataStoreCa, dataStoreIdentity } from '../pages/security/certificates/certificates';
import accountModels from '../pages/security/accounts/account-store';

// INPUT: string to hash
// OUTPUT: hash string
async function digestMessage(message: string) {
  let hashHex = null;
  try {
    const encoder = new TextEncoder();
    const data = encoder?.encode?.(message);
    if (!data) {
      return null;
    }
    const hash = await crypto?.subtle?.digest?.('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hash)); // convert buffer to byte array
    hashHex = hashArray?.map((b) => b?.toString?.(16)?.padStart(2, '0'))?.join(''); // convert bytes to hex string
  } catch (ex) {
    return null;
  }
  return hashHex;
}
async function createAnonymizedDeviceId() {
  return digestMessage(systemStatus.serialNumber + ':' + systemInfo.hostname ?? systemInfo.deviceIp);
}

export function useFullStory(isFSEnabled: boolean, systemInfoFetched: boolean) {
  const debug = false;

  useEffect(() => {
    const isInit = FS.isInitialized();
    // console.log('useFullStory', isFSEnabled, !isInit, systemInfoFetched);
    if (isFSEnabled && !isInit && systemInfoFetched) {
      FS.init({ orgId: 'o-1EQT7G-na1', devMode: Config.isDebug, debug: debug }, ({ sessionUrl }) => {
        /* eslint-disable-next-line no-console */
        console.info(`Started FullStory session: ${sessionUrl}`, systemInfo);
        FS.setUserVars({
          product_str: constant.productName,
          environment_str: process.env.NODE_ENV,
          version_str: systemInfo.version,
          platform_str: 'web',
        });
      });
    }
  }, [isFSEnabled, systemInfoFetched]);
}

export function useSendFsStats(
  isFSEnabled: boolean,
  audioEncodersFetched: boolean,
  videoEncodersFetched: boolean,
  streamsFetched: boolean,
) {
  useEffect(() => {
    if (isFSEnabled && FS.isInitialized() && audioEncodersFetched && videoEncodersFetched && streamsFetched) {
      createAnonymizedDeviceId()?.then((deviceIdAnonymised) => {
        const streams = streamModels;
        const videoEncoder = videoEncoders;
        const audioEncoder = audioEncoders;

        const content = {
          name: 'MX4E Device Statistics',
          properties: {
            device_id: deviceIdAnonymised.substring(0, 16), // unique identifier per device to filter
            device_personality: systemInfo.personality, // the devices personality setting
            device_firmware: systemStatus.firmwareVersion, // the devices installed firmware version
            streams_total: streams.length, // number all configured streams
            streams_active: streams.filter((s) => s.stats?.state === StreamState.RUNNING).length, // number of streaming streams
            encoders_video_active: videoEncoder.filter((s) => s.stats?.state === VideoEncoderState.WORKING).length, // number of active video encoders
            encoders_audio_active: audioEncoder.filter((s) => s.stats?.state === AudioEncoderState.WORKING).length, // number of active audio encoders
          },
        };
        FS.event('trackEvent', content);
      });
    }
  }, [isFSEnabled, audioEncodersFetched, videoEncodersFetched, streamsFetched]);
}

export function useSendFsMetadataStats(isFSEnabled: boolean, metadataFetched: boolean) {
  useEffect(() => {
    if (isFSEnabled && FS.isInitialized() && metadataFetched) {
      createAnonymizedDeviceId()?.then((deviceIdAnonymised) => {
        const metadata = metadataModels;
        const content = {
          name: 'MX4E Device Metadata Statistics',
          properties: {
            device_id: deviceIdAnonymised.substring(0, 16), // unique identifier per device to filter
            metadata_total: metadata.length, // number of all metadata items --useSendFsMetadataStats (isFSEnabled: boolean, metadataFetched: boolean)
            metadata_active: metadata.filter((s) => s.state === MetadataState.WORKING).length, // number of active metadata items
          },
        };
        FS.event('trackEvent', content);
      });
    }
  }, [isFSEnabled, metadataFetched]);
}

export function useSendFsAdInsertionStats(isFSEnabled: boolean, adInsertionFetched: boolean) {
  useEffect(() => {
    if (isFSEnabled && FS.isInitialized() && adInsertionFetched) {
      createAnonymizedDeviceId()?.then((deviceIdAnonymised) => {
        const adInsertion = scteModels;
        const content = {
          name: 'MX4E Device Add Insertion Statistics',
          properties: {
            device_id: deviceIdAnonymised.substring(0, 16), // unique identifier per device to filter
            ad_insertions_in_use: adInsertion.filter((s) => s.state === ScteState.WORKING).length, // number of active ad insertions
          },
        };
        FS.event('trackEvent', content);
      });
    }
  }, [isFSEnabled, adInsertionFetched]);
}

export function useSendFsLogoStats(isFSEnabled: boolean, logoFetched: boolean) {
  useEffect(() => {
    if (isFSEnabled && FS.isInitialized() && logoFetched) {
      createAnonymizedDeviceId()?.then((deviceIdAnonymised) => {
        const logo = logoStore;
        const logoSettings = logoSettingsStore;
        const content = {
          name: 'MX4E Device Logo Statistics',
          properties: {
            device_id: deviceIdAnonymised.substring(0, 16), // unique identifier per device to filter
            logo_overlay_total: logo.length, // number of configured overlays --useSendFsLogoStats
            logo_overlay_active: logoSettings.filter((s) => s.display === true && s.fileName?.length > 0).length, // number of enable overlays
          },
        };
        FS.event('trackEvent', content);
      });
    }
  }, [isFSEnabled, logoFetched]);
}

export function useSendFsAdminStats(
  isFSEnabled: boolean,
  licensesFetched: boolean,
  certificatesFetched: boolean,
  accountFetched: boolean,
) {
  useEffect(() => {
    if (isFSEnabled && FS.isInitialized() && (licensesFetched || certificatesFetched || accountFetched)) {
      createAnonymizedDeviceId()?.then((deviceIdAnonymised) => {
        const content: any = {
          name: 'MX4E Device Admin Statistics',
          properties: {
            device_id: deviceIdAnonymised.substring(0, 16), // unique identifier per device to filter
          },
        };
        if (licensesFetched) {
          content.properties.licenses = licensesList?.list?.length; // number of installed licenses
        }

        if (certificatesFetched) {
          content.properties.identity_certificates = dataStoreIdentity?.list?.length; // number of listed identity certificates
          content.properties.identity_certificates_type_selfsigned = dataStoreIdentity?.list?.filter((c) =>
            c.type?.toLowerCase()?.includes('self'),
          )?.length;
          content.properties.identity_certificates_type_casigned = dataStoreIdentity?.list?.filter((c) =>
            c.type?.toLowerCase()?.includes('ca'),
          )?.length;
          content.properties.ca_certificates = dataStoreCa?.list?.length; // number of listed ca certificates
        }

        if (accountFetched) {
          content.properties.user_accounts = accountModels.length;
        }
        FS.event('trackEvent', content);
      });
    }
  }, [isFSEnabled, licensesFetched, certificatesFetched, accountFetched]);
}
